/**
 * THE DEFINITION LIFECYCLE — the domain reading of `definitionStatus`.
 *
 * WHAT `definitionStatus` IS, FROM THE FROZEN CONTRACT
 *   `CapabilityDefinitionSnapshotV1.DefinitionStatus { DRAFT, PUBLISHED, DEACTIVATED }`,
 *   documented as "Definition lifecycle only; never an allowed-action vocabulary."
 *
 * WHAT THIS MODULE DECIDES
 *   That the field is read AS A LIFECYCLE, and that a lifecycle is not readiness,
 *   not eligibility, not availability, not authorization and not execution
 *   permission. FE-1 Stage Work Item §7.4 puts that decision here rather than in a
 *   component: "a component may not decide ... what a contract status means."
 *
 * WHAT THIS MODULE DOES NOT DECIDE
 *   It does not re-value the field, does not add a member, does not rename a
 *   member, does not map a member to a second vocabulary and does not infer
 *   anything from it. `DefinitionLifecycle` below is an ALIAS of the mirror's own
 *   field type, not a second type, and `DEFINITION_LIFECYCLE_LABELS` is keyed by
 *   exactly the three frozen members — a fourth member would fail the typecheck.
 *
 * `PUBLISHED` DOES NOT MEAN READY, AND `DEACTIVATED` DOES NOT MEAN FORBIDDEN
 *   FE-1 Stage Work Item §6.4. A published definition is a published definition;
 *   whether anything may be done with it is not a fact this read plane carries and
 *   is not a conclusion this layer is permitted to draw.
 */

import type { CapabilityDefinitionSnapshotV1 } from '../consumer';

/**
 * The definition lifecycle.
 *
 * An ALIAS, not a new type: it is literally the mirror's `definitionStatus` field
 * type. Declaring a structurally identical second type here would create a second
 * place for the vocabulary to drift, which is what the alias avoids.
 */
export type DefinitionLifecycle = CapabilityDefinitionSnapshotV1['definitionStatus'];

/** What the presentation is allowed to say about one lifecycle member. */
export interface DefinitionLifecycleReading {
  /** The member's own name, carried as text. Never a readiness or permission word. */
  readonly label: string;
  /** What the member means, stated so it cannot be read as readiness. */
  readonly statement: string;
}

/**
 * The three lifecycle members, each with its own reading.
 *
 * Keyed by the frozen vocabulary, so a member added to the mirror and not to this
 * table is a compile error, and a member added here that the mirror does not carry
 * is a compile error too.
 */
export const DEFINITION_LIFECYCLE_LABELS: Readonly<
  Record<DefinitionLifecycle, DefinitionLifecycleReading>
> = Object.freeze({
  DRAFT: {
    label: 'Draft',
    statement:
      'This definition is in the draft lifecycle stage. A draft is not readiness, not eligibility and not permission to act.',
  },
  PUBLISHED: {
    label: 'Published',
    statement:
      'This definition is in the published lifecycle stage. Published is not readiness, not eligibility, not availability and not permission to act.',
  },
  DEACTIVATED: {
    label: 'Deactivated',
    statement:
      'This definition is in the deactivated lifecycle stage. Deactivated is not readiness, not eligibility and not permission to act, and it is not a prohibition either.',
  },
});

/**
 * The statement that must accompany any lifecycle reading.
 *
 * Rendered as text beside the lifecycle label so the distinction is carried
 * textually and not by a badge colour (FE-1 Stage Work Item §11.2 check 4, T-3).
 */
export const LIFECYCLE_IS_NOT_READINESS_STATEMENT =
  'Definition lifecycle only. It is not operational availability, readiness, ' +
  'eligibility, assignment, commitment or permission to act.';

/**
 * Read the lifecycle of a snapshot.
 *
 * A pure pass-through: it returns the mirror's own field unchanged. It exists so
 * that a caller reads the field AS a lifecycle, through a name that says so,
 * rather than reaching into the snapshot and having to remember what the field is.
 */
export function definitionLifecycleOf(
  snapshot: CapabilityDefinitionSnapshotV1,
): DefinitionLifecycle {
  return snapshot.definitionStatus;
}

/* ------------------------------------------------------------------------- *
 * Compile-time assertions of the lifecycle reading's shape.
 * ------------------------------------------------------------------------- */

/** Fails to compile unless `T` is exactly `true`. */
type AssertTrue<T extends true> = T;

/** `true` only when the two unions have exactly the same members. */
type IsExactly<TLeft, TRight> = [TLeft] extends [TRight]
  ? [TRight] extends [TLeft]
    ? true
    : false
  : false;

/** `true` only when the two types share no member. */
type IsDisjoint<TLeft, TRight> = [Extract<TLeft, TRight>] extends [never] ? true : false;

/** The lifecycle is exactly the three frozen members, and no fourth. */
export type LifecycleIsExactlyTheFrozenThree = AssertTrue<
  IsExactly<DefinitionLifecycle, 'DRAFT' | 'PUBLISHED' | 'DEACTIVATED'>
>;

/**
 * `Definition != Readiness`: none of these words is a lifecycle member, so no
 * lifecycle value can carry one and no `switch` over the lifecycle can produce one.
 */
export type LifecycleCarriesNoReadinessOrPermissionMember = AssertTrue<
  IsDisjoint<
    DefinitionLifecycle,
    'READY' | 'ELIGIBLE' | 'OPERATIONAL' | 'AUTHORIZED' | 'EXECUTABLE' | 'ALLOWED' | 'PERMITTED'
  >
>;

/** The reading table covers exactly the frozen members. */
export type LifecycleLabelsCoverTheVocabulary = AssertTrue<
  IsExactly<keyof typeof DEFINITION_LIFECYCLE_LABELS, DefinitionLifecycle>
>;
